/**
 * Access control model
 * --------------------
 * - Public routes:    anyone (no wrapper).
 * - ProtectedRoute:   any signed-in user; otherwise redirect to /auth.
 * - AdminRoute:       signed-in AND has the 'admin' role in user_roles.
 *
 * The database is the source of truth: every sensitive table has RLS
 * policies that call public.has_role(auth.uid(), 'admin'). These route
 * guards are UX-only — they keep non-admins out of admin screens, but
 * data is also protected at the DB layer.
 */
import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { AdminSessionGuard } from "@/components/auth/AdminSessionGuard";
import { usePermissions } from "@/hooks/usePermissions";

export function ProtectedRoute() {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="container py-20 text-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to={`/auth?redirect=${encodeURIComponent(loc.pathname)}`} replace />;
  return <Outlet />;
}

export function AdminRoute() {
  const { user, isAdmin, loading, refreshRole } = useAuth();
  const { permissions, loading: permLoading } = usePermissions();
  const loc = useLocation();
  // Staff = full admin OR any volunteer with at least one delegated permission.
  const isStaff = isAdmin || permissions.some((p) => p.can_view || p.can_edit);

  // Re-verify the admin role on every entry into an /admin/* route so a
  // revoked admin cannot continue navigating the admin UI on a stale session.
  useEffect(() => {
    if (user) void refreshRole();
  }, [loc.pathname, user, refreshRole]);

  if (loading || (user && permLoading)) return <div className="container py-20 text-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to={`/auth?redirect=${encodeURIComponent(loc.pathname)}`} replace />;
  if (!isStaff) return <Navigate to="/" replace />;
  return (
    <>
      <AdminSessionGuard isStaff={isStaff} />
      <Outlet />
    </>
  );
}

